import { LLMChoice } from '@/redux/types/room'
import axiosInstance from './apiClient'
import {
  AdvisorDataWithFiles,
  MyFile,
  ReprocessDocumentsResponse,
} from '@/redux/types/configuration'

export const uploadFiles = (
  files: File[],
  directory: string = '',
  chatbotid: string,
  options?: { target?: 'advisor' | 'room' | 'dummy_bot'; roomId?: string }
) => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files[]', file))
  formData.append('directory', directory)
  if (options?.target === 'room') {
    if (!options.roomId) {
      throw new Error('roomId is required when target is room')
    }
    formData.append('roomid', options.roomId)
    formData.append('target', 'room')
  } else if (options?.target === 'dummy_bot') {
    formData.append('chatbotid', chatbotid)
    formData.append('target', 'dummy_bot')
  } else {
    formData.append('chatbotid', chatbotid)
  }

  return axiosInstance.post<MyFile[]>('/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const fetchAdvisorData = (chatbotid: string) => {
  return axiosInstance.get(`/oge/chatbot/advisors/${chatbotid}/`)
}

export const saveAdvisorData = (
  chatbotid: string,
  options?: {
    chatbotname?: string
    advisorData?: AdvisorDataWithFiles
    chatbot_llm?: LLMChoice
    mcp_enabled?: boolean
  }
) => {
  const requestBody: any = {}

  if (options?.chatbotname !== undefined) {
    requestBody.chatbotname = options.chatbotname
  }

  if (options?.advisorData !== undefined) {
    Object.assign(requestBody, options.advisorData)
  }

  if (options?.chatbot_llm !== undefined) {
    requestBody.chatbot_llm = options.chatbot_llm
  }

  if (options?.mcp_enabled != null) {
    requestBody.mcp_enabled = options.mcp_enabled
  }

  return axiosInstance.put(`/oge/chatbot/advisors/${chatbotid}/`, requestBody)
}

export const deleteAdvisorData = (chatbotid: string) => {
  return axiosInstance.delete(`/oge/chatbot/advisors/${chatbotid}/`)
}

export const cloneAdvisor = (chatbotid: string) => {
  return axiosInstance.post(`/oge/chatbot/advisors/${chatbotid}/clone/`)
}

export const deleteFile = (
  fileId: number,
  options: {
    chatbotId?: string
    target?: 'advisor' | 'room' | 'dummy_bot'
    roomId?: string
  }
) => {
  const { chatbotId, target = 'advisor', roomId } = options
  if (target === 'room' && roomId) {
    return axiosInstance.delete(
      `/files/${fileId}/delete/?target=room&roomid=${encodeURIComponent(roomId)}`
    )
  }
  const id = chatbotId ?? ''
  if (target === 'dummy_bot' && id) {
    return axiosInstance.delete(
      `/files/${fileId}/delete/?target=dummy_bot&chatbotid=${encodeURIComponent(id)}`
    )
  }
  return axiosInstance.delete(`/files/${fileId}/delete/?chatbotid=${encodeURIComponent(id)}`)
}

/**
 * Delete-all RAG documents (issue #563). Response shape:
 * { success, namespaces_cleared, documents_deleted, documents_unlinked }.
 */
export const deleteAllAdvisorDocuments = (chatbotId: string) => {
  return axiosInstance.delete(
    `/oge/chatbot/advisors/${encodeURIComponent(chatbotId)}/documents/`
  )
}

export const deleteWorldSetupDocuments = (roomId: string) => {
  return axiosInstance.delete(
    `/oge/rooms/${encodeURIComponent(roomId)}/advisor-config/documents/`
  )
}

export const deleteAllRoomAdvisorsDocuments = (roomId: string) => {
  return axiosInstance.delete(
    `/oge/rooms/${encodeURIComponent(roomId)}/advisor-documents/`
  )
}

export const deleteAllRoomDocuments = (roomId: string) => {
  return axiosInstance.delete(
    `/oge/rooms/${encodeURIComponent(roomId)}/documents/`
  )
}

export const reprocessDocuments = async (
  chatbotId: string,
  documentIds?: number[],
  target: 'advisor' | 'dummy_bot' = 'advisor'
): Promise<ReprocessDocumentsResponse> => {
  const payload: { document_ids?: number[] } = {}
  if (documentIds !== undefined) {
    payload.document_ids = documentIds
  }
  const endpoint =
    target === 'dummy_bot'
      ? `/oge/chatbot/dummy-bots/${chatbotId}/reprocess-documents/`
      : `/oge/chatbot/advisors/${chatbotId}/reprocess-documents/`
  const response = await axiosInstance.post<ReprocessDocumentsResponse>(endpoint, payload)
  return response.data
}
